import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTodayLoginPreview } from "../../../redux/slices/dashSlice";
import {
  PersonOutlined,
  AccessTimeOutlined,
  DescriptionOutlined,
  SaveOutlined,
  GroupAddOutlined,
  FormatListNumberedOutlined,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import LoadingIndicator from "../../utils/LoadingIndicator";

const TodayLogin = forwardRef(({},ref) => {
  const dispatch = useDispatch();
  const { todayLoginPreview,getStatus } = useSelector((state) => state.dash);

  useEffect(() => {
    dispatch(fetchTodayLoginPreview());
  }, [dispatch]);

  useImperativeHandle(ref, () => ({
    downloadExcel: () => {
      if (!todayLoginPreview || todayLoginPreview.length === 0) return;
  
      const dataToExport = todayLoginPreview.map((client, index) => ({
        'S.No.': index + 1,
        'User Name': client.name,
        'Last Login': new Date(client.login_time)?.toLocaleString("en-GB"),
        'Total Quotations': client.quotation_count,
        'Todays Quotations': client.todayQuotationTotal,
        'Total Drafts': client.draft_count,
        'Todays Drafts': client.todayDratTotal,
        'Total Clients': client.client_count,
        'Todays Clients': client.todayClientTotal
      }));
  
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clients');
      XLSX.writeFile(wb, 'todays_login.xlsx');
    }
  }));

  return (
    <>
           <LoadingIndicator show={getStatus === "pending"}/>
    
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-label="today's login table">
            <TableHead sx={{ background: "#04121f", color: "white" }}>
              <TableRow>
                <TableCell sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormatListNumberedOutlined /> S. No.
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonOutlined /> User Name
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccessTimeOutlined /> Last Login
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}
                  align="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                  >
                    <DescriptionOutlined /> Total Quotations
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}
                  align="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                  >
                    <DescriptionOutlined /> Today's Quotations
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}
                  align="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                  >
                    <SaveOutlined />Total Drafts
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}
                  align="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                  >
                    <SaveOutlined />Today Drafts
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}
                  align="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                  >
                    <GroupAddOutlined /> Total Clients
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: "bold" ,whiteSpace: "nowrap"}}
                  align="center"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    justifyContent="center"
                  >
                    <GroupAddOutlined /> Today's Clients
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {todayLoginPreview?.length > 0 ? (
                todayLoginPreview.map((user, index) => (
                  <TableRow
                    key={ `${user.name}-${index}-${user.login_time}` }
                    hover
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" },
                      "&:hover": { backgroundColor: "#eef2ff" },
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <TableCell sx={{ fontWeight: "bold", color: "#4c66f5" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {user.name}
                    </TableCell>
                    <TableCell>
                      {new Date(user.login_time)?.toLocaleString("en-GB")}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.quotation_count}
                        size="small"
                        sx={{
                          backgroundColor: "#e0e7ff",
                          color: "#000",
                          fontWeight: "semibold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.todayQuotationTotal}
                        size="small"
                        sx={{
                          backgroundColor: "#e0e7ff",
                          color: "#000",
                          fontWeight: "semibold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.draft_count}
                        size="small"
                        sx={{
                          backgroundColor: "#d1fae5",
                          color: "#000",
                          fontWeight: "semibold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.todayDraftTotal}
                        size="small"
                        sx={{
                          backgroundColor: "#d1fae5",
                          color: "#000",
                          fontWeight: "semibold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.client_count}
                        size="small"
                        sx={{
                          backgroundColor: "#fee2e2",
                          color: "#000",
                          fontWeight: "semibold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.todayClientTotal}
                        size="small"
                        sx={{
                          backgroundColor: "#fee2e2",
                          color: "#000",
                          fontWeight: "semibold",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      No user logins found today.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
    </>
  );
});

export default TodayLogin;
